import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import DashIcon from '../../components/ui/DashIcon'
import { deliveriesAPI } from '../../api/deliveries'
import type { Delivery, PaginatedResponse } from '../../types'

const TUNISIA_PATH = 'M 230 30 L 280 35 L 310 60 L 305 95 L 340 110 L 360 145 L 360 195 L 365 260 L 355 300 L 340 340 L 360 380 L 410 420 L 395 460 L 350 485 L 305 490 L 280 470 L 250 440 L 210 410 L 180 380 L 140 340 L 120 290 L 130 240 L 155 200 L 170 160 L 180 120 L 200 80 L 215 50 Z'

const STOP_POINTS = [
  { x: 280, y: 90  }, { x: 290, y: 110 }, { x: 275, y: 130 },
  { x: 295, y: 150 }, { x: 285, y: 170 }, { x: 270, y: 155 },
  { x: 260, y: 175 }, { x: 300, y: 120 }, { x: 285, y: 140 }, { x: 265, y: 165 },
]

const openGoogleMaps = (address: string) =>
  window.open(`https://www.google.com/maps/dir//${encodeURIComponent(address)}`, '_blank')

export default function LivreurRoute() {
  const navigate = useNavigate()

  const { data, isLoading } = useQuery<PaginatedResponse<Delivery>>({
    queryKey: ['route-deliveries'],
    queryFn: async () => {
      const res = await deliveriesAPI.getAll({ status: 'in_transit', limit: 10 })
      return res
    },
  })

  const stops = data?.data ?? []
  const total = data?.total ?? 0

  const startRoute = () => {
    if (stops.length === 0) return
    openGoogleMaps(stops[0].dropoff.address)
  }

  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Ma route</h1>
          <p>Itinéraire optimisé pour les livraisons du jour.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={startRoute} disabled={stops.length === 0}>
            <DashIcon name="zap" size={14} /> Démarrer
          </button>
        </div>
      </div>

      <div className="detail-grid" style={{ '--detail-cols': '1fr 360px', gap: 18 } as CSSProperties}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: 18, borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '.78rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>Trajet du jour</div>
              <div style={{ fontFamily: 'Space Grotesk', fontSize: '1.4rem', fontWeight: 700, color: 'var(--navy-900)' }}>
                {isLoading ? 'Chargement…' : `${total} arrêt${total !== 1 ? 's' : ''}`}
              </div>
            </div>
            <button className="btn btn-primary" onClick={startRoute} disabled={stops.length === 0}>
              <DashIcon name="zap" size={14} /> Démarrer
            </button>
          </div>
          <div className="map-mini" style={{ height: 440 }}>
            <svg viewBox="0 0 500 520" preserveAspectRatio="xMidYMid meet">
              <path d={TUNISIA_PATH} fill="rgba(124,179,66,.06)" stroke="rgba(124,179,66,.4)" strokeWidth="1.2" strokeDasharray="3 3" />
              {stops.slice(0, 10).map((_, i) => {
                const p = STOP_POINTS[i] ?? STOP_POINTS[i % STOP_POINTS.length]
                return (
                  <g key={i}>
                    <circle
                      cx={p.x} cy={p.y} r="8"
                      fill={i === 0 ? 'var(--green-500)' : 'var(--navy-900)'}
                      stroke="#fff" strokeWidth="2"
                    />
                    <text x={p.x + 12} y={p.y + 4} fill="rgba(255,255,255,.8)" fontSize="10" fontFamily="JetBrains Mono" fontWeight="700">
                      {i + 1}
                    </text>
                  </g>
                )
              })}
              {stops.length > 1 && (
                <polyline
                  points={stops.slice(0, 10).map((_, i) => {
                    const p = STOP_POINTS[i] ?? STOP_POINTS[i % STOP_POINTS.length]
                    return `${p.x},${p.y}`
                  }).join(' ')}
                  fill="none" stroke="#7CB342" strokeWidth="2" strokeDasharray="4 3" opacity=".7"
                />
              )}
            </svg>
          </div>
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
            <h3>Arrêts du jour</h3>
            <p style={{ color: 'var(--muted)', fontSize: '.82rem', marginTop: 2 }}>
              {isLoading ? 'Chargement…' : `${total} arrêt${total !== 1 ? 's' : ''} en attente`}
            </p>
          </div>

          {isLoading ? (
            <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 32, fontSize: '.84rem' }}>Chargement…</div>
          ) : stops.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40, fontSize: '.84rem' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
              Aucune livraison en cours !
            </div>
          ) : stops.map((s, i) => (
            <div
              key={s._id}
              style={{
                display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12,
                padding: '14px 18px',
                borderBottom: i < stops.length - 1 ? '1px solid var(--line)' : undefined,
                alignItems: 'center',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: i === 0 ? 'var(--green-500)' : 'var(--navy-50)',
                color: i === 0 ? '#fff' : 'var(--navy-700)',
                display: 'grid', placeItems: 'center',
                fontWeight: 700, fontSize: '.85rem', fontFamily: 'Space Grotesk', flexShrink: 0,
              }}>{i + 1}</div>
              <div>
                <div style={{ color: 'var(--navy-900)', fontWeight: 600, fontSize: '.88rem' }}>
                  {s.dropoff.address}
                </div>
                <div className="mono" style={{ color: 'var(--muted)', fontSize: '.75rem', marginTop: 2 }}>
                  {s.trackingCode} · {s.dropoff.contactName ?? '—'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => openGoogleMaps(s.dropoff.address)}
                  title="Naviguer"
                >
                  <DashIcon name="truck" size={12} />
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => navigate(`/livreur/colis/${s._id}`)}
                  title="Détails"
                >
                  <DashIcon name="more" size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
